// Sponsor.st visitor script: the one tag a site owner pastes.
//   <script src="https://cdn.sponsor.st/s.js" data-site="EMBED_KEY" async></script>
// load → config → place → render → track.
// It only touches its own <sl-slot> / <sl-row> boxes and, while a top bar shows, the top of the site's
// pinned header. Each unit is drawn in its box's own shadow root, out of the site's CSS's reach.
// Every error is caught and reported: the site must never break.

import { load, pagePath } from './config.js';
import { css, data, find } from './dom.js';
import { makeRoom } from './pinned.js';
import { boxFor, boxOf, fit, spot, tidyRows } from './place.js';
import { CSS, HEIGHTS, creative, mount, placeholder } from './render.js';
import { themer } from './theme.js';
import { tracker } from './track.js';

const me = /** @type {HTMLScriptElement | null} */ (document.currentScript);
const key = me?.dataset.site;

if (me && key && !('SponsorSt' in window)) {   // pasted twice: run once
    const hub = { version: SST_VERSION };
    Object.defineProperty(window, 'SponsorSt', { value: hub });
    start(me, key, hub);
}

/**
 * Use storage; null when it's blocked (sandboxed frame, strict privacy settings).
 * A function declaration, so it exists before start() runs above.
 * @template T
 * @param {() => T} use
 * @returns {T | null}
 */
function read(use) {
    try {
        return use();
    } catch {
        return null;
    }
}

/** @param {HTMLScriptElement} me @param {string} key @param {Record<string, unknown>} hub  the one global */
function start(me, key, hub) {
    const api = new URL(me.src).origin + '/x';   // neutral path names, so ad blockers leave them alone
    // Edit mode: "Edit slots on your site" opened this page with #sst=<one-time token>, or an edit
    // session is running. editor.js redeems the token and the server checks every save.
    // #sst-preview: the editor's 📱 Preview frame, a plain visitor view (it shares the tab's sessionStorage).
    const preview = location.hash === '#sst-preview';
    const editing = !preview && (/^#sst=/.test(location.hash) || !!read(() => sessionStorage.getItem('sst-edit:' + key)));
    // the owner's own visits count nothing: while editing, and afterwards (editor.js sets this flag)
    const owner = editing || !!read(() => localStorage.getItem('sst-owner:' + key));
    const track = tracker(api, key, owner);
    // the owner (editing, previewing, or looking at the site after editing) sees what was just saved, never a
    // cached answer: visitors' answers may be up to 60 seconds old
    const fresh = owner || preview;
    /** @param {() => unknown} fn */
    const safe = fn => {
        try {
            Promise.resolve(fn()).catch(track.error);
        } catch (e) {
            track.error(e);
        }
    };
    /** @type {import('./config.js').Config} */
    let cfg = { slots: [], look: {} };
    /** @type {import('./config.js').Slot[]}  edit mode only: slots visitors don't see, and the live preview */
    let extra = [];
    let path = pagePath();
    let config = load(api, key, path, fresh);   // fetch while the page is still loading

    // The page-load ping waits until the page has settled (loaded, then 2 s without changes, at most
    // 10 s), so slow and single-page sites don't report spots as missing. A visitor who leaves sooner
    // still counts as a view; that ping says it wasn't settled, and the server counts no misses from it.
    let view = 0, unsent = false;
    let seen = { found: /** @type {string[]} */ ([]), missing: /** @type {string[]} */ ([]) };
    /** @param {boolean} settled @param {string} [click]  a sponsor clicked before the ping went out @param {number} [entry]  the leaderboard entry clicked */
    const ping = (settled, click, entry) => {
        if (!unsent) return;
        unsent = false;
        track.page(path, seen.found, seen.missing, settled, click, entry);
    };
    // Sponsor links go straight to the sponsor; a beacon counts the click (plain, Enter or middle
    // button). Caught before the site's own handlers, which may stop the event. Price tags don't count.
    // The click happens inside a slot's shadow root: the event's first target is the link itself, and
    // its root's host is the slot.
    /** @param {MouseEvent} e */
    const clicked = e => {
        const t = e.composedPath()[0], a = t instanceof Element ? t.closest('.sl-c a[href], a.sl-c[href]') : null, root = a?.getRootNode();
        const id = root instanceof ShadowRoot ? /** @type {HTMLElement} */ (root.host).dataset.sl : undefined;
        if (!id || (e.type === 'auxclick' && e.button !== 1)) return;
        const entry = Number(/** @type {HTMLElement} */ (a).dataset.e) || undefined;   // a leaderboard entry
        if (unsent) ping(false, id, entry);
        else track.click(id, entry);
    };
    /** One of our lazy chunks (b: leaderboards, e: the editor), from where s.js came from. @param {'b' | 'e'} name */
    const chunk = name => new Promise((ok, fail) => document.head.append(Object.assign(document.createElement('script'), { src: new URL(`/embed/${name}.v${SST_VERSION}.js`, me.src).href, onload: ok, onerror: fail })));
    // Leaderboards are drawn by their own chunk, loaded once and only on a page that has one.
    /** @type {Promise<unknown> | null} */
    let boardChunk = null;
    /** @param {HTMLElement} box  the slot @param {HTMLElement} u  where it draws @param {import('./config.js').Board} b */
    const drawBoard = (box, u, b) => {
        boardChunk ||= chunk('b');
        boardChunk.then(() => {
            if (u.firstChild || !box.isConnected) return;
            /** @type {(box: HTMLElement, b: import('./config.js').Board) => void} */ (hub.board)(u, b);
            data(box, 'on', '1');
            track.watch(box);
        }).catch(track.error);
    };
    for (const type of ['click', 'auxclick']) addEventListener(type, e => safe(() => clicked(/** @type {MouseEvent} */ (e))), true);
    /** @param {() => void} fn */
    const whenSettled = fn => {
        const watch = () => {
            let quiet = 0;
            const done = () => {
                changes.disconnect();
                clearTimeout(quiet);
                clearTimeout(cap);
                safe(fn);
            };
            const changes = new MutationObserver(() => {
                clearTimeout(quiet);
                quiet = setTimeout(done, 2000);
            });
            const cap = setTimeout(done, 10000);
            changes.observe(document.body, { childList: true, subtree: true });
            quiet = setTimeout(done, 2000);
        };
        if (document.readyState === 'complete') watch();
        else addEventListener('load', watch, { once: true });
    };

    /**
     * The slots this page shows: the config's, with the editor's copy of a slot winning (one without a
     * sponsor or placeholder keeps the config's: a booked slot being edited), plus the editor's own.
     */
    const shown = () => {
        /** @param {import('./config.js').Slot} s */
        const own = s => s.sponsor || s.placeholder || s.board ? s : { ...cfg.slots.find(c => c.id === s.id), ...s };
        // in the list's order (the editor's copy in its slot's place), so slots sharing a spot keep theirs
        return [...cfg.slots.map(c => extra.find(s => s.id === c.id) || c), ...extra.filter(s => !cfg.slots.some(c => c.id === s.id))].map(s => extra.includes(s) ? own(s) : s);
    };

    function render() {
        const slots = shown(), colours = themer(cfg.look), found = [], missing = [];
        /** How many slots share each spot. @type {Map<string, number>} */
        const spots = new Map();
        for (const s of slots) {
            if (s.format !== 'bar') spots.set(spot(s), (spots.get(spot(s)) || 0) + 1);
        }
        for (const box of document.querySelectorAll('sl-slot')) {
            if (!slots.some(s => s.id === /** @type {HTMLElement} */ (box).dataset.sl)) box.remove();
        }
        let top = null;
        for (const s of slots) {
            const format = s.format || '', pinned = format === 'bar';
            const box = boxFor(s, pinned, !pinned && (spots.get(spot(s)) || 0) > 1);
            if (!box) {
                missing.push(s.id);
                continue;
            }
            found.push(s.id);
            const u = mount(box, s.board ? '' : format);
            if (!u.firstChild) {   // a new box, or one the site's markup rewrite made again without its shadow root
                if (s.board) drawBoard(box, u, s.board);
                else {
                    const unit = s.sponsor ? creative(s.sponsor, format) : s.placeholder ? placeholder(s.placeholder, format) : null;
                    if (unit) u.append(unit);
                    data(box, 'on', unit ? '1' : '');
                    if (s.sponsor) track.watch(box);
                }
            }
            const t = colours(box);
            data(box, 'slTheme', t.dark ? 'dark' : 'light');
            css(box, t.vars);
            if (!pinned && !s.board) fit(box, s, HEIGHTS[format] || 0);
            const child = /** @type {HTMLElement | null} */ (u.firstElementChild);
            if (pinned && child) top = child;
        }
        tidyRows(spots, found);
        document.body.classList.toggle('sl-hastop', !!top);
        makeRoom(top);
        seen = { found, missing };
    }

    // A new page view: the first load, or a route change in a single-page app. True once it's drawn.
    async function show() {
        const forPath = path, next = await config;
        if (path !== forPath || !next) return false;   // the visitor moved on meanwhile, or there's nothing to show
        cfg = next;
        for (const e of document.querySelectorAll('sl-slot, sl-row')) e.remove();
        track.reset();
        render();
        const thisView = ++view;
        unsent = true;
        whenSettled(() => {
            if (view === thisView) ping(true);
        });
        return true;
    }

    // React, Vue, Svelte… can remove our boxes when they re-render, and a markup rewrite (innerHTML,
    // jQuery's .html()) makes them again without their shadow root: put them back, but give up on a page
    // that keeps doing it, so we never fight the site in an endless loop.
    function keepAlive() {
        let queued = false;
        /** @type {number[]} */
        let adds = [];
        const gone = () => shown().some(s => {
            const b = boxOf(s.id);
            return b ? !b.shadowRoot : s.format === 'bar' || !!find(s.selector);
        });
        const watcher = new MutationObserver(() => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => safe(() => {
                queued = false;
                if (!gone()) return;
                const now = Date.now();
                adds = adds.filter(t => now - t < 10000).concat(now);
                if (adds.length > 40) {
                    watcher.disconnect();
                    console.warn('Sponsor.st: this page keeps removing sponsor slots, so we stopped adding them back.');
                    return;
                }
                render();
            }));
        });
        watcher.observe(document.body, { childList: true, subtree: true });

        // dark mode switches (class or data-theme on <html> or <body>) change colours without a reload;
        // our own sl-hastop class on <body> is not a theme change
        const own = (/** @type {string | null} */ v) => String(v || '').replace(/\bsl-hastop\b/g, '').trim();
        const themes = new MutationObserver(records => {
            if (records.some(r => own(r.oldValue) !== own(/** @type {Element} */ (r.target).getAttribute(r.attributeName || '')))) {
                requestAnimationFrame(() => safe(render));
            }
        });
        for (const e of [document.documentElement, document.body]) {
            themes.observe(e, { attributes: true, attributeFilter: ['class', 'data-theme'], attributeOldValue: true });
        }

        // phone rotated, window resized, late CSS: widths and the side gap may change
        let wait = 0;
        const later = () => {
            clearTimeout(wait);
            wait = setTimeout(() => safe(render), 150);
        };
        addEventListener('resize', later);
        addEventListener('load', later);

        // single-page apps change the URL without reloading
        const moved = () => {
            if (pagePath() === path) return;
            ping(false);   // the page we're leaving still counts as a view
            path = pagePath();
            config = load(api, key, path, fresh);
            safe(show);
        };
        for (const k of /** @type {const} */ (['pushState', 'replaceState'])) {
            const original = history[k];
            history[k] = function (...args) {
                const result = original.apply(this, args);
                safe(moved);
                return result;
            };
        }
        addEventListener('popstate', () => safe(moved));
        // the dashboard's edit link opened in a tab already on this page only changes the hash: start again
        addEventListener('hashchange', () => /^#sst=/.test(location.hash) && location.reload());
    }

    const go = () => safe(async () => {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
        if (editing) {
            hub.edit = {
                api,
                key,
                /** @param {import('./config.js').Slot[]} slots */
                extra: slots => {
                    extra = slots;
                    safe(render);
                },
                reload: () => {
                    config = load(api, key, path, fresh);
                    return show().catch(e => (track.error(e), false));
                },
            };
            chunk('e').catch(track.error);
        }
        await show();
        keepAlive();
        addEventListener('pagehide', () => ping(false));
    });
    if (document.readyState === 'loading') addEventListener('DOMContentLoaded', go);
    else go();
}
